import {
  getTrendingStocksListData,
  IResearchCallItem,
  ITrendingStocksListResponse,
} from "../../../services/TradingMallService";
import type {
  TrendingStocksCardData,
  TrendingStocksDurationFilter,
  TrendingStocksListResult,
  TrendingStocksPrimaryTab,
  TrendingStocksQueryParams,
} from "../types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function stringifyNumber(value: unknown): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}`;
  }
  if (typeof value === "string") {
    return value.trim() ? value.trim() : undefined;
  }
  return undefined;
}

function withPercent(value: unknown): string | undefined {
  const normalized = stringifyNumber(value);
  if (!normalized) return undefined;
  return normalized.includes("%") ? normalized : `${normalized}%`;
}

function formatDateTime(value: string | undefined): { date: string; time: string } {
  if (!value) {
    return { date: "--", time: "--" };
  }
  const [datePart, timePart] = value.trim().split(" ");
  if (!datePart) return { date: "--", time: "--" };

  const [yearRaw, monthRaw, dayRaw] = datePart.split("-");
  const year = Number(yearRaw);
  const monthIndex = Number(monthRaw) - 1;
  const day = Number(dayRaw);

  if (!year || monthIndex < 0 || monthIndex > 11 || !day) {
    return { date: datePart, time: timePart?.slice(0, 8) ?? "--" };
  }

  return {
    date: `${day.toString().padStart(2, "0")} ${MONTHS[monthIndex]} ${year}`,
    time: timePart?.slice(0, 8) ?? "--",
  };
}

function mapInstrumentType(instType: string | undefined): string {
  const normalized = (instType ?? "").toUpperCase();
  if (normalized.includes("OPT")) return "OPTION";
  if (normalized.includes("FUT")) return "FUTURE";
  return "CASH";
}

function normalizeCallDurationFromTermType(
  termType: string | undefined,
): TrendingStocksDurationFilter {
  const normalized =
    typeof termType === "string"
      ? termType.toLowerCase().replace(/[^a-z0-9]/g, "")
      : "";

  // Backend variants we currently see:
  // - sterm/mterm/lterm/all
  // - short/mid/long (legacy/alternate)
  if (normalized === "sterm" || normalized.includes("short")) return "short_term";
  if (normalized === "mterm" || normalized.includes("mid")) return "mid_term";
  if (normalized === "lterm" || normalized.includes("long")) return "long_term";
  return "all_calls";
}

function mapTermType(termType: string | undefined): string {
  switch (normalizeCallDurationFromTermType(termType)) {
    case "short_term":
      return "SHORT TERM";
    case "mid_term":
      return "MID TERM";
    case "long_term":
      return "LONG TERM";
    default:
      return "ALL CALLS";
  }
}

function mapRequestType(tab: TrendingStocksPrimaryTab): "F&O" | "STK" {
  return tab === "fo" ? "F&O" : "STK";
}

function mapEntityFromDuration(duration: TrendingStocksDurationFilter): string {
  switch (duration) {
    case "short_term":
      return "sterm";
    case "mid_term":
      return "mterm";
    case "long_term":
      return "lterm";
    default:
      return "all";
  }
}

function getResponseList(response: ITrendingStocksListResponse): IResearchCallItem[] {
  const directResearchCalls = response.researchCalls;
  if (Array.isArray(directResearchCalls)) {
    return directResearchCalls;
  }
  const nestedResearchCalls = response.data?.researchCalls;
  if (Array.isArray(nestedResearchCalls)) {
    return nestedResearchCalls;
  }

  const directStockList = response.stockList;
  if (Array.isArray(directStockList)) {
    return directStockList as IResearchCallItem[];
  }
  const nestedStockList = response.data?.stockList;
  return Array.isArray(nestedStockList)
    ? (nestedStockList as IResearchCallItem[])
    : [];
}

function mapResearchCallToCard(
  item: IResearchCallItem,
  index: number,
  page: number,
  initialExpandedCards: number,
): TrendingStocksCardData {
  const exchange = item.exchange === 2 ? 2 : 0;
  const token = Number(item.token ?? 0);
  const ltp = Number(item.ltp ?? 0);
  const prevClose = Number(item.prevClose ?? 0);
  const valueChange = prevClose ? ltp - prevClose : 0;
  const percentageChange = prevClose ? (valueChange / prevClose) * 100 : 0;
  const { date, time } = formatDateTime(item.calldt);
  const normalizedRedirectUrl = item.redirectURL?.startsWith("/")
    ? item.redirectURL
    : `/${item.redirectURL ?? ""}`;
  const callType = (item.calltype ?? "").toUpperCase();

  return {
    id: `${item.symbol ?? "stock"}-${item.exchange ?? 0}-${token}-${item.calldt ?? index}`,
    symbol: item.symbol ?? "--",
    stockName: item.compname ?? item.symbol ?? "--",
    exchange,
    token,
    nseToken: exchange === 0 ? token : 0,
    bseToken: exchange === 2 ? token : 0,
    exchId: exchange === 2 ? "2" : "0",
    redirectURL: normalizedRedirectUrl,
    ltp: stringifyNumber(item.ltp) ?? "--",
    valueChange: valueChange.toFixed(2),
    percentageChange: `${percentageChange.toFixed(2)}%`,
    gainSoFar: withPercent(item.returnSoFar) ?? "--",
    callDateLabel: date,
    callTimeLabel: time,
    callTypeLabel: mapTermType(item.termType),
    callStatusLabel: (item.status ?? "--").toUpperCase(),
    callOptionLabel: mapInstrumentType(item.instType),
    buyPrice: stringifyNumber(item.recprice),
    targetPrice: stringifyNumber(item.targetprice1),
    stopLoss: stringifyNumber(item.slprice1),
    potentialReturns: withPercent(item.potentialReturns),
    tradeAction: callType === "SELL" ? "sell" : "buy",
    isExpandedDefault: page === 1 && index < initialExpandedCards,
  };
}

export async function fetchTrendingStocksList(
  params: TrendingStocksQueryParams & {
    entity: string;
    tab: TrendingStocksPrimaryTab;
    duration: TrendingStocksDurationFilter;
  },
): Promise<TrendingStocksListResult | null> {
  const {
    entity,
    tab,
    duration,
    page = 1,
    limit = 10,
    initialExpandedCards = 1,
  } = params;
  const requestEntity = mapEntityFromDuration(duration) || entity;

  try {
    const response = await getTrendingStocksListData({
      entity: requestEntity,
      tab,
      type: mapRequestType(tab),
      exchId: 0,
      page,
      limit,
      sort: "amount_high_to_low",
      filters: {
        index: [],
        sector: [],
        mktcap: [],
        alphabetic: [],
      },
    });

    const rawList = getResponseList(response);
    const filteredList =
      duration === "all_calls"
        ? rawList
        : rawList.filter(
            (item) => normalizeCallDurationFromTermType(item.termType) === duration,
          );

    const items = filteredList.map((item, index) =>
      mapResearchCallToCard(item, index, page, initialExpandedCards),
    );
    const totalCount =
      response.totalCount ?? response.data?.totalCount ?? items.length;
    const showMore =
      response.showMore ??
      response.data?.showMore ??
      (totalCount > 0 ? page * limit < totalCount : items.length >= limit);

    return {
      items,
      totalCount,
      showMore,
    };
  } catch {
    return null;
  }
}
